<template>
	<table class="PriceMatrix" v-if="modelValue && ticketCategories && ticketTypes">
		<tr>
			<th></th>
			<th v-for="column, tc of activeColumns" :key="columnId(column)">
				<div class="fixedWidth" :class="{ wide: usePriceType && column.priceType }">
					<ReductionField dense
						v-model="activeColumns[tc].ticketCategory"
						:serviceProviderOptionsOverride="categoryOptionsFor(column)"
						:canAddNew="false"
					>
						<template #append-item>
							<v-btn @click="removeColumn(column)" variant="text"><v-icon>mdi-minus</v-icon> {{ $t('text.remove') }}</v-btn>
						</template>
					</ReductionField>
					<!-- the price type belongs to the ticket option, it is not editable here -->
					<div v-if="usePriceType && column.priceType" class="priceTypeLabel" :title="priceTypeLabel(column.priceType)">
						{{ priceTypeLabel(column.priceType) }}
					</div>
				</div>
			</th>
			<th>
				<v-icon @click="addColumn()" v-if="activeColumns.length < availableColumns.length">mdi-plus</v-icon>
			</th>
		</tr>
		<tr v-for="ticketType, tt of activeTicketTypes" :key="ticketType.sys.id">
			<th>
				<div class="fixedWidth" style="width: 120px">
					<!-- TODO: pass availableTicketTypes as options -->
					<TicketTypeField dense
						v-model="activeTicketTypes[tt]"
						:serviceProviderOptionsOverride="availableTicketTypes"
						:canAddNew="false"
					>
						<template #append-item>
							<v-btn @click="removeTicketType(ticketType)" variant="text"><v-icon>mdi-minus</v-icon> {{ $t('text.remove') }}</v-btn>
						</template>
					</TicketTypeField>
				</div>
			</th>
			<td v-for="column of activeColumns" :key="columnId(column)"
				:data-ticketType="ticketType.sys.id"
				:data-ticketCategory="column.ticketCategory.sys.id"
				:data-priceType="column.priceType"
			>
				<!-- TODO: we may want to move this block to a dedicated comp to isolate the key concern -->
				{{ ensurePriceModel(column, ticketType) }}
				<NumberField class="field"
					v-if="model[ keyForColumn(column, ticketType) ] && baseMatrix[ keyForColumn(column, ticketType) ]"
					v-model="model[ keyForColumn(column, ticketType) ].price"
					:unit="currency"
					:min="0"
					:disabled="disabled"
					:field="dummyFieldDefinition"
					width="100%"
				/>
			</td>
			<td></td>
		</tr>
		<tr v-if="activeTicketTypes.length < ticketTypes.length">
			<th>
				<v-icon @click="addTicketType()" v-if="activeTicketTypes.length < availableTicketTypes.length">mdi-plus</v-icon>
			</th>
			<td :colspan="ticketCategories.length + 1" />
		</tr>
	</table>
</template>

<script lang="ts">
import NumberField from '../fields/NumberField.vue'
import ReductionField from '../serviceDesigner/ReductionField.vue'
import TicketTypeField from '../serviceDesigner/TicketTypeField.vue'
import { TicketPrice } from '../../../../shared/seasonal-prices'

type Matrix = { [ key: string ]: TicketPrice }

// MYS-7206: a column is not just a ticket category anymore. for trade hub clients with the
// price type feature the same category appears once per price type (FIT / group / generic),
// because those are distinct tickets with distinct prices. when usePriceType is false every
// descriptor has priceType undefined and the grid collapses back to its original shape.
export type Column = { ticketCategory: any, priceType?: string }

export const matrixCalculatorMixin = {
	props: {
		basePrices: Array,
		disabled: Boolean,
		usePriceType: Boolean,
	},
	data: () => ({
		model: {}, // Matrix
		// these can not be computed as we will push into them
		activeColumns: [] as Column[],
		activeTicketTypes: [],
	}),
	computed: {
		modelMatrix() {
			return this.toMatrix(this.modelValue)
		},
		baseMatrix() {
			return this.toMatrix(this.basePrices)
		},
		currency() {
			return this.$store.state.selectedClient?.fields?.currency?.de || '€'
		},
		availableColumns(): Column[] {
			return this.getColumnsForMatrix(this.baseMatrix)
		},
		availableTicketTypes() {
			return this.getTicketOptionsForMatrix('ticketType', this.baseMatrix)
		},
	},
	methods: {
		keyFor(ticketCategory, ticketType, priceType?: string) {
			const key = (ticketCategory?.sys?.id ?? ticketCategory) + '|' + (ticketType?.sys?.id ?? ticketType)
			// keep the key byte-identical to what it always was when there is no price type,
			// so existing models keep matching
			return priceType ? key + '|' + priceType : key
		},
		keyForColumn(column: Column, ticketType) {
			return this.keyFor(column?.ticketCategory, ticketType, column?.priceType)
		},
		// stable identity of a column, used for :key and for comparing active vs available
		columnId(column: Column) {
			const id = column?.ticketCategory?.sys?.id ?? column?.ticketCategory
			return column?.priceType ? id + '|' + column.priceType : id
		},
		priceTypeLabel(priceType?: string) {
			if (!priceType) return ''
			// reuses the keys already defined for the ticket option price type selector
			const keys = { fit: 'text.priceTypeFit', group: 'text.priceTypeGroup', generic: 'text.priceTypeGeneric' }
			return keys[priceType] ? this.$t(keys[priceType]) : priceType
		},
		toMatrix(value: TicketPrice[]): Matrix {
			if (!value) return {}
			const r: Matrix = {}
			value.forEach((price: TicketPrice) => {
				r[ this.keyFor(price.ticketCategory, price.ticketType, price.priceType) ] = price
			})
			return r
		},
		// TODO: if the value changes, we should maintain "hidden" prices
		//       dont return them in toValue though
		//       CURRENTLY hidden prices are sent to the server
		//       maybe that is fine? the server must only ignore them when sending to peak..
		//       could be confusing though if some old price shows up later..
		toModel(value: TicketPrice[]): Matrix {
			const r: Matrix = this.toMatrix(value)
			// add missing prices (prices defined in the basePrices but not in the model)
			this.basePrices?.forEach?.((price: TicketPrice) => {
				const key = this.keyFor(price.ticketCategory, price.ticketType, price.priceType)
				if (r[ key ]) return
				r[ key ] = { ...price, price: null }
			})
			return r
		},
		toValue(model: Matrix): TicketPrice[] {
			// filter out any non-active columns/types
			return Object.values(model)
				.filter((price: TicketPrice) => this.activeColumns.find((c: Column) =>
					c.ticketCategory?.sys?.id == price.ticketCategory && (c.priceType ?? null) === (price.priceType ?? null)))
				.filter((price: TicketPrice) => this.activeTicketTypes.find(o => o.sys.id == price.ticketType))
		},
		// with this trick we make sure, that the model contains proper objects as they are needed
		ensurePriceModel(column: Column, ticketType) {
			const key = this.keyForColumn(column, ticketType)
			if (!this.model[ key ]) {
				this.model[ key ] = { ticketCategory: column.ticketCategory, ticketType, price: null }
				if (column.priceType) this.model[ key ].priceType = column.priceType
			}
		},
		getTicketOptionsForMatrix(type: 'ticketCategory' | 'ticketType', matrix: Matrix, andMatrix?: Matrix): any[] {
			const all: string[] = Object.values(matrix).map((price: TicketPrice) => price?.[type])
			const and: string[] | null= andMatrix ? Object.values(andMatrix).map((price: TicketPrice) => price?.[type]) : null
			let collection: any[] = []
			if (type == 'ticketCategory') collection = this.ticketCategories
			if (type == 'ticketType') collection = this.ticketTypes
			return collection.filter(o => all.includes(o.sys.id) && (!and || and.includes(o.sys.id)))
		},
		// the distinct (ticketCategory, priceType) pairs present in a matrix, optionally
		// intersected with a second one (used to keep the active set within the available set)
		getColumnsForMatrix(matrix: Matrix, andMatrix?: Matrix): Column[] {
			const columnsIn = (m: Matrix) => Object.values(m)
				.filter((price: TicketPrice) => !!price)
				.map((price: TicketPrice) => ({
					ticketCategory: this.ticketCategories.find(o => o.sys.id == price.ticketCategory),
					priceType: this.usePriceType ? price.priceType : undefined,
				}))
				.filter((c: Column) => !!c.ticketCategory)

			const and: string[] | null = andMatrix ? columnsIn(andMatrix).map(c => this.columnId(c)) : null
			const seen = new Set<string>()
			const r: Column[] = []
			for (const column of columnsIn(matrix)) {
				const id = this.columnId(column)
				if (seen.has(id)) continue
				if (and && !and.includes(id)) continue
				seen.add(id)
				r.push(column)
			}
			return r
		},
	},
	mounted() {
		this.model = this.toModel(this.modelValue)

		this.activeColumns = this.getColumnsForMatrix(this.modelMatrix, this.baseMatrix)
		//this.availableTicketTypes = this.getTicketOptionsForMatrix('ticketType', this.baseMatrix)
		this.activeTicketTypes = this.getTicketOptionsForMatrix('ticketType', this.modelMatrix, this.baseMatrix)
	},
}

export default {
	mixins: [ matrixCalculatorMixin ],
	components: { ReductionField, TicketTypeField, NumberField },
	props: {
		ticketCategories: Array,
		ticketTypes: Array,
		// [ { ticketCategory: 'adult', ticketType: '1', price: 0 }, ... ]
		modelValue: Array,
	},
	data: () => ({
		dummyFieldDefinition: { validations: [] },
	}),
	watch: {
		model: {
			handler(n, o) {
				const v = this.toValue(n)
				if (JSON.stringify(o) == JSON.stringify(v)) return
				this.$emit('update:modelValue', this.toValue(n))
			},
			deep: true,
		},
		modelValue(n, o) {
			if (JSON.stringify(n) == JSON.stringify(o)) return
			this.model = this.toModel(n)
		},
	},
	methods: {
		// the categories offered in a column's dropdown. switching a column to another category
		// must stay within the price type that column represents, otherwise the column would
		// point at a ticket option that does not exist.
		categoryOptionsFor(column) {
			const columns = this.availableColumns
				.filter(c => (c.priceType ?? null) === (column?.priceType ?? null))
			return this.uniqueCategories(columns.map(c => c.ticketCategory))
		},
		uniqueCategories(categories) {
			const seen = new Set()
			return categories.filter(o => {
				if (!o || seen.has(o.sys.id)) return false
				seen.add(o.sys.id)
				return true
			})
		},
		// if no id is passed, add the first available column
		addColumn(id: string = undefined) {
			const active = this.activeColumns.map(c => this.columnId(c))
			const column = this.availableColumns.find(c =>
				!id && !active.includes(this.columnId(c)) || id == this.columnId(c))
			if (!column) return
			this.activeColumns.push({ ...column })
		},
		addTicketType(id: string = undefined) {
			const tt = this.availableTicketTypes.find(o => !id && !this.activeTicketTypes.includes(o) || id == o.sys.id)
			if (!tt) return
			this.activeTicketTypes.push(tt)
		},
		removeColumn(column) {
			this.activeColumns = this.activeColumns.filter(o => o != column)
			// TODO: this doesnt work yet - should we even remove the data from the working model?
			//       i think we should only remove from the outbound model..
		},
		removeTicketType(ticketType) {
			this.activeTicketTypes = this.activeTicketTypes.filter(o => o != ticketType)
			//for (const p in this.modelValue) {
			//	if (p.endsWith('|' + ticketType.sys.id)) delete this.modelValue[p]
			//}
		},
	},
	mounted() {
		// MYS-7206: a new season opens with every configured ticket visible, so the user can see
		// and price all of them at once. it used to open as a 1-cell table, which hid the other
		// price type variants of a category behind repeated clicks on "+" - with price types
		// there can be a lot of them, and nothing hinted that they existed.
		if (this.modelValue && this.modelValue.length == 0) {
			this.activeColumns = this.availableColumns.map(c => ({ ...c }))
			this.activeTicketTypes = [ ...this.availableTicketTypes ]
		}
	},
}
</script>

<style scoped>
input { background: white; max-width: 90px; padding: 2px 4px; border: 1px solid #ddd; border-radius: 4px; }

table { border-collapse: collapse; }
th { text-align: left; }
th,
td { white-space: nowrap; padding: 8px; }

/* borders */
th,
td { border: 1px solid #ddd; }
th:first-child,
td:first-child { border-left-color: transparent; }
th:last-child,
td:last-child { border-right-color: transparent; width: 100% !important; }
tr:first-child th,
tr:first-child td { border-top-color: transparent; }
tr:last-child th,
tr:last-child td { border-bottom-color: transparent; }

.fixedWidth { width: 80px; overflow: hidden; text-overflow: ellipsis; }
/* the category alone fits in 80px, but with a price type underneath it needs the room */
.fixedWidth.wide { width: 130px; }

.priceTypeLabel { font-size: 11px; font-weight: normal; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.field { padding: 0; }
</style>